/**
 * EmbeddedCheckout — the Stripe embedded checkout form in a dialog.
 *
 * Stripe.js loads from js.stripe.com in index.html (PCI: never bundle it).
 * The backend creates the Checkout Session and returns its client secret.
 * After a confirmed payment, Stripe sends the browser to the session's
 * return_url (/billing?checkout=...).
 */
import { useEffect, useState } from 'react';
import { Box, Callout, Dialog, Flex, Spinner, Text } from '@radix-ui/themes';

// Appearance configured in Stripe Checkout Studio.
const APPEARANCE = {
  theme: 'stripe',
  labels: 'auto',
  inputs: 'spaced',
  variables: {
    borderRadius: '4px',
    colorBackground: '#ffffff',
    colorDanger: '#df1b41',
    colorPrimary: '#0570de',
    colorSuccess: '#00c853',
    colorText: '#30313d',
    fontFamily: 'default',
    fontSizeBase: '16px',
    spacingUnit: '4px',
  },
};

// The checkout form SDK is a Stripe.js beta with no published types yet.
interface CheckoutActions {
  confirm: (options: { formConfirmEvent: unknown }) => Promise<unknown>;
}
interface CheckoutForm {
  mount: (selector: string) => void;
  unmount?: () => void;
  on: (event: 'confirm', handler: (event: unknown) => void) => void;
}
interface CheckoutFormSdk {
  createForm: (options: { layout: 'expanded' }) => CheckoutForm;
  loadActions: () => Promise<{ type: string; actions: CheckoutActions }>;
}
interface StripeWithCheckoutForm {
  initCheckoutFormSdk: (options: { clientSecret: Promise<string>; appearance: object }) => CheckoutFormSdk;
}

declare global {
  interface Window {
    Stripe?: (key: string, options?: { betas?: string[] }) => StripeWithCheckoutForm;
  }
}

const CONTAINER_ID = 'checkout-form';

interface Props {
  title: string;
  // Returns the client secret. null closes the dialog.
  load: (() => Promise<string>) | null;
  onClose: () => void;
}

const EmbeddedCheckout: React.FC<Props> = ({ title, load, onClose }) => {
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!load) {
      return undefined;
    }
    setError(null);
    setReady(false);
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!window.Stripe || !publishableKey) {
      setError('Checkout is not set up yet. Contact support.');
      return undefined;
    }

    // Radix renders the dialog content in a portal on the next frame, so
    // wait one frame for the container to exist.
    let form: CheckoutForm | null = null;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (cancelled || !document.getElementById(CONTAINER_ID)) {
        return;
      }
      const stripe = window.Stripe!(publishableKey, { betas: ['custom_checkout_payment_form_1'] });
      const clientSecret = load();
      clientSecret.catch((err: { response?: { data?: { message?: string } } }) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not start checkout. Try again.');
        }
      });
      const checkout = stripe.initCheckoutFormSdk({ clientSecret, appearance: APPEARANCE });
      form = checkout.createForm({ layout: 'expanded' });
      form.mount(`#${CONTAINER_ID}`);
      checkout.loadActions().then((result) => {
        if (cancelled || result.type !== 'success') {
          return;
        }
        setReady(true);
        form?.on('confirm', async (event) => {
          try {
            await result.actions.confirm({ formConfirmEvent: event });
          } catch (err) {
            console.error('Payment confirmation error:', err);
          }
        });
      }).catch(() => {
        if (!cancelled) {
          setError('Could not start checkout. Try again.');
        }
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      form?.unmount?.();
    };
  }, [load]);

  return (
    <Dialog.Root open={load !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Content maxWidth="520px">
        <Dialog.Title>{title}</Dialog.Title>
        {error ? (
          <Callout.Root color="red"><Callout.Text>{error}</Callout.Text></Callout.Root>
        ) : (
          <>
            {!ready && (
              <Flex align="center" gap="2" mb="3">
                <Spinner /><Text size="2" color="gray">Loading secure checkout…</Text>
              </Flex>
            )}
            <Box id={CONTAINER_ID} />
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default EmbeddedCheckout;
